import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {queryClient} from "@/queryClient";
import type {COUNTRY_FILTERS} from "@/store/constants";
import {create} from "@/store/createStore";
import type {TCampaignStatus, TCampaignType, TListCampaign, TNonFunctionKeys, TPaginated} from "@/store/types";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {type DebouncedFuncLeading, debounce} from "lodash";
import omit from "lodash/omit";
import qs from "qs";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export const statuses = ["planned", "open", "preparation", "active", "reporting", "complete", "paused"] as const;

type TState = {
  searchParams: string;
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  filters: TFilters;
  buildSearchParams(): string;
  setFilters<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V): void;
  setSearchParams(searchParams: string): void;
};

type TFilters = {
  company_name: string;
  campaign_name: string;
  campaign_type: TCampaignType | "all";
  event: boolean | null;
  location: (typeof COUNTRY_FILTERS)[number] | null;
  interests: number[];
  on_going: boolean | null;
  status: Omit<TCampaignStatus, "draft"> | null;
  reward: "product" | "cash" | null;
  bonus_pool: boolean | null;
  pro: boolean | null;
};

type TFormattedFilters = Omit<TFilters, "campaign_type"> & {
  [K in TCampaignType]?: string;
};

const initialState = {
  searchParams: "",
  filters: {
    company_name: "",
    campaign_name: "",
    campaign_type: "all",
    event: null,
    location: null,
    interests: [],
    on_going: null,
    status: null,
    reward: null,
    bonus_pool: null,
    pro: null,
  },
} as Pick<TState, TNonFunctionKeys<TState>>;

const useCampaignFilterStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        debouncedSearchParams: debounce(() => {
          get().buildSearchParams();
        }, 1000),
        buildSearchParams: () => {
          const filters = get().filters;
          let formattedFilters: TFormattedFilters = omit(filters, ["campaign_type"]);

          if (filters.campaign_type !== "all") {
            formattedFilters[filters.campaign_type] = "True";
          }

          return qs.stringify(formattedFilters, {arrayFormat: "comma"});
        },
        setSearchParams: searchParams =>
          set(
            state => {
              state.searchParams = searchParams;
            },
            false,
            "useCampaignFiltersStore/buildSearchParams",
          ),

        setFilters: (key, value) => {
          get().debouncedSearchParams.cancel();

          set(
            state => {
              state.filters[key] = value;
            },
            false,
            "useCampaignFiltersStore/setFilters",
          );

          let searchParams = get().buildSearchParams();
          let timeout = 1000;
          const cachedQuery = queryClient.getQueryData<InfiniteData<AxiosResponse<TPaginated<TListCampaign>>>>(
            CAMPAIGN_KEYS.qCampaignList(searchParams),
          );
          if (!!cachedQuery?.pages) {
            timeout = 0;
          }

          set(
            state => {
              state.debouncedSearchParams = debounce(() => {
                get().setSearchParams(searchParams);
              }, timeout);
            },
            false,
            "useCampaignFiltersStore/setDebouncedSearchParams",
          );

          get().debouncedSearchParams();
        },
      }),
      {name: "useCampaignFiltersStore"},
    ),
  ),
);

export default useCampaignFilterStore;
